import { useCallback, useMemo } from 'react'
import type { Session, User as SupabaseUser } from '@supabase/supabase-js'
import { useNavigate } from 'react-router-dom'
import { useUser, useIsAuthenticated, useSetUser, useSetError, useClearError, useLogout, useSetLoading, useAuthStore } from '../../../stores/authStore'
import { LoginCredentials, SignupCredentials, UserProfile, AuthApi, SignupResponse } from '../../../shared/api/auth'
import { supabase } from '../../../shared/utils/supabase'

export function useAuth() {
  const navigate = useNavigate()
  const user = useUser()
  const isAuthenticated = useIsAuthenticated()
  const setUser = useSetUser()
  const setError = useSetError()
  const clearError = useClearError()
  const storeLogout = useLogout()
  const setLoading = useSetLoading()

  const getAuthToken = useCallback(async (): Promise<string | null> => {
    return localStorage.getItem('access_token')
  }, [])

  const authApi = useMemo(() => new AuthApi(getAuthToken), [getAuthToken])

  // After login/signup the backend returns tokens. Hand them to the Supabase
  // client so it can own the session lifecycle (auto-refresh timer, persistence).
  const persistSessionTokens = useCallback(async (accessToken: string, refreshToken: string) => {
    localStorage.setItem('access_token', accessToken)
    localStorage.setItem('refresh_token', refreshToken)
    await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
    supabase.realtime.setAuth(accessToken)
  }, [])

  const syncUserProfile = useCallback(async (): Promise<UserProfile> => {
    try {
      const userProfile = await authApi.getProfile()
      setUser(userProfile)
      return userProfile
    } catch (error) {
      throw error
    }
  }, [authApi, setUser])

  const completeEmailVerification = useCallback(async (session: Session | null, user: SupabaseUser | null): Promise<UserProfile> => {
    if (!session || !user) {
      throw new Error('Invalid verification session')
    }
    try {
      await persistSessionTokens(session.access_token, session.refresh_token)
      return await syncUserProfile()
    } catch (error) {
      throw error
    }
  }, [persistSessionTokens, syncUserProfile])

  const login = useCallback(async (
    credentials: LoginCredentials,
    options?: { redirectTo?: string | null }
  ): Promise<UserProfile> => {
    setLoading(true)
    clearError()
    try {
      const authResult = await authApi.login(credentials)
      await persistSessionTokens(authResult.access_token, authResult.refresh_token)
      const userProfile = await syncUserProfile()
      // Use redirect param for post-login navigation (e.g. back to share link to join as member);
      // otherwise route to onboarding if the server-side flag is null, else dashboard.
      const dest = options?.redirectTo
      const isValidRedirect = dest && dest.startsWith('/') && !dest.startsWith('//')
      const fallback = userProfile?.onboarding_completed_at ? '/dashboard' : '/onboarding'
      navigate(isValidRedirect ? dest : fallback, { replace: true })
      return userProfile
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      setError(errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }, [navigate, setError, clearError, setLoading, authApi, persistSessionTokens, syncUserProfile])

  const signup = useCallback(async (credentials: SignupCredentials): Promise<SignupResponse> => {
    setLoading(true)
    clearError()
    try {
      const authResult = await authApi.signup(credentials)
      if (authResult.requiresEmailConfirmation) {
        return authResult
      }
      if (authResult.access_token && authResult.refresh_token && authResult.user) {
        await persistSessionTokens(authResult.access_token, authResult.refresh_token)
        await syncUserProfile()
      }
      return authResult
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Signup failed'
      setError(errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }, [setError, clearError, setLoading, authApi, persistSessionTokens, syncUserProfile])

  const loginWithGoogle = useCallback(async () => {
    const redirect = new URLSearchParams(window.location.search).get('redirect')
    const callbackPath = redirect
      ? `/auth/callback?redirect=${encodeURIComponent(redirect)}`
      : '/auth/callback'
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}${callbackPath}`
      }
    })
    // signInWithOAuth redirects the browser — nothing runs after this line
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // Continue with logout even if the backend call fails
    } finally {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      supabase.realtime.setAuth(null)
      storeLogout()
      // signOut triggers SIGNED_OUT in AuthProvider's onAuthStateChange
      await supabase.auth.signOut()
      navigate('/', { replace: true })
    }
  }, [navigate, storeLogout, authApi])

  // Manually trigger a token refresh via the Supabase client. With autoRefreshToken
  // enabled this is rarely needed — Supabase refreshes proactively before expiry.
  const refreshToken = useCallback(async (): Promise<string | null> => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession()
      if (error || !session) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        storeLogout()
        return null
      }
      localStorage.setItem('access_token', session.access_token)
      localStorage.setItem('refresh_token', session.refresh_token)
      supabase.realtime.setAuth(session.access_token)
      return session.access_token
    } catch {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      storeLogout()
      return null
    }
  }, [storeLogout])

  // Returns a valid access token, asking the Supabase client for the current
  // session when the stored token is expired or about to expire.
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const token = localStorage.getItem('access_token')
      if (!token) return null

      try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        const now = Math.floor(Date.now() / 1000)
        const expiresAt = payload.exp || 0
        const timeUntilExpiry = expiresAt - now

        if (expiresAt && timeUntilExpiry < 300) {
          const { data: { session } } = await supabase.auth.getSession()
          if (session) {
            localStorage.setItem('access_token', session.access_token)
            localStorage.setItem('refresh_token', session.refresh_token)
            return session.access_token
          }
          storeLogout()
          return null
        }

        return token
      } catch {
        const { data: { session } } = await supabase.auth.getSession()
        return session?.access_token ?? null
      }
    } catch {
      return null
    }
  }, [storeLogout])

  const getToken = getAccessToken

  const isBetaUser = useMemo(() => {
    if (!user) return false
    return user.user_metadata?.is_beta_user === true
  }, [user])

  return {
    user,
    isAuthenticated,
    isBetaUser,
    isLoading: useAuthStore(state => state.isLoading),
    login,
    loginWithGoogle,
    signup,
    logout,
    refreshToken,
    getAccessToken,
    getToken,
    clearError,
    completeEmailVerification
  }
}

export { useUser, useIsAuthenticated, useSetUser, useSetError, useClearError, useLogout } from '@/stores'
